/** The map editor.
 *
 * A leaf, like every other screen in this package: it holds the draft it is
 * editing and nothing else. Compiling a draft into a `NewGameSetup`, writing a
 * file, and starting a game are all the host's, because all three need the
 * kernel's map builders and the shell may not call a rule module (I10). What
 * the host hands over is three callbacks and whatever the last compile said was
 * wrong with the map.
 *
 * The layout is the one every tile editor has converged on and for the same
 * reasons: tools down the left where the hand rests, the board in the middle at
 * whatever zoom fits, and the properties of whatever is selected down the right
 * so that changing a thing does not mean losing sight of it.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { ContentRegistry, PlayerKind, TerrainKind } from "@game/core";
import { TERRAINS } from "@game/core";

import { usePolishStyles } from "../screens/polish.js";
import { ScreenStyles } from "../screens/primitives.js";
import { titleCase } from "../screens/format.js";
import { useAtlas } from "../hooks/useAtlas.js";
import { BitmapText } from "../theme/bitmapText.js";
import { useElementSize } from "../theme/components.js";
import { editorFixture } from "./fixture.js";
import { terrainFillKey } from "./ground.js";
import type { DraftIssue, EditorDraft, DraftPlayer } from "./model.js";
import {
  EDITOR_COLOURS, addObject, addPlayer, blankDraft, canRedo, canUndo, draftIssues,
  draftObjectAt, editorReducer, fillLevel, hasErrors, newHistory, paintTerrain,
  parsePayloadValue, patchPlayer, payloadText, removeObjectAt, removePlayer,
  resizeDraft, setObjectOwner, setObjectPayload, setObjectPayloadAll, setPlayerHero,
  setPlayerTown, silentOverrides, terrainAtCell,
} from "./model.js";
import type { DraftObject, DraftPayload } from "./model.js";
import { presetsFor } from "./presets.js";
import type { GroundArt, PaintCursor, PaintView } from "./paint.js";
import {
  DEFAULT_ZOOM, ZOOM_STEPS, cellFromPixel, centreOn, clampView, coverZoom,
  draftPlayerColour, fitZoom, minimapCell, minimapSize, paintDraft, paintMinimap,
  terrainCensus, terrainSwatch, visibleCells,
} from "./paint.js";
import type { PaletteEntry } from "./palette.js";
import { buildPalette, filterPalette, paletteCount } from "./palette.js";

// -------------------------------------------------------------------- tools

export type EditorTool = "terrain" | "object" | "erase" | "town" | "hero" | "inspect";

const TOOL_LABEL: Readonly<Record<EditorTool, string>> = Object.freeze({
  terrain: "Terrain",
  object: "Object",
  erase: "Erase",
  town: "Capital",
  hero: "Hero",
  inspect: "Inspect",
});

const TOOL_HINT: Readonly<Record<EditorTool, string>> = Object.freeze({
  terrain: "Paint the ground. The brush is a disc; hold and drag.",
  object: "Place the selected object. One per cell.",
  erase: "Take the object under the pointer off the map.",
  town: "Put the selected player's capital here. A player has one.",
  hero: "Put the selected player's starting hero here.",
  inspect: "Read what is under the pointer without changing it.",
});

export const BRUSH_SIZES: readonly { readonly id: string; readonly radius: number }[] =
  Object.freeze([
    { id: "1", radius: 0 },
    { id: "3", radius: 1 },
    { id: "5", radius: 2 },
    { id: "9", radius: 4 },
  ]);

// -------------------------------------------------------------------- props

export interface MapEditorProps {
  readonly content: ContentRegistry | null;
  /** The map to open on. A blank one is made when this is absent. */
  readonly initial?: EditorDraft | undefined;
  /** Terrains a town may not stand on; the host reads them off the registry. */
  readonly waterTerrains?: ReadonlySet<string> | undefined;
  /** Write the draft out as a map file. */
  readonly onSaveFile?: ((draft: EditorDraft) => void) | undefined;
  /** Open a map file and hand back a draft. */
  readonly onOpenFile?: (() => void) | undefined;
  /** Start a game on this map. Only offered when nothing is an error. */
  readonly onPlay?: ((draft: EditorDraft) => void) | undefined;
  /** Fill the board from the random generator, so an author can start from one. */
  readonly onGenerate?: ((draft: EditorDraft) => void) | undefined;
  /** Whatever the host's last compile or save reported. */
  readonly notice?: string | null | undefined;
  readonly onClose: () => void;
}

const STYLE: Readonly<Record<string, CSSProperties>> = {
  body: { display: "flex", gap: "8px", padding: "8px", minHeight: 0, height: "100%" },
  rail: {
    width: "216px", flex: "0 0 auto", display: "flex", flexDirection: "column",
    gap: "8px", minHeight: 0, overflowY: "auto",
  },
  stage: {
    flex: "1 1 auto", minWidth: 0, minHeight: 0, position: "relative",
    overflow: "hidden",
    // The board paints its own stone floor and its own carved lip, so the
    // frame here is only the outer keyline that seats the panel in the screen.
    boxShadow: "inset 0 0 0 1px #0a0805, 0 0 0 1px rgba(168,128,31,0.45)",
    background: "#130f0a",
  },
  canvas: { display: "block", width: "100%", height: "100%", cursor: "crosshair" },
  swatches: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "4px" },
  /**
   * The one-line readout in the corner of the board.
   *
   * A plate rather than loose text over the ground: the string sits on art the
   * build does not control, and the choice at that point is a keyline round
   * every glyph or a plate under the whole line. A plate is cheaper to read and
   * is what the classic's own hover strip is.
   */
  readout: {
    position: "absolute", left: 8, bottom: 8, padding: "4px 9px 5px",
    display: "flex", alignItems: "center", gap: "8px",
    background: "linear-gradient(180deg, rgba(38,30,18,0.94), rgba(16,12,7,0.94))",
    boxShadow: "inset 0 0 0 1px rgba(168,128,31,0.55), 0 1px 0 rgba(0,0,0,0.8)",
    color: "#efe2c2", pointerEvents: "none",
  },
  mixBar: {
    display: "flex", height: "10px", marginTop: "2px", overflow: "hidden",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.75)",
  },
};

/** A caption in the shared bitmap face, which is what every other screen sets
 *  its captions in. Prose stays in the serif; a label never is one. */
const Cap = (props: { readonly children: string }): JSX.Element => (
  <BitmapText face="text" scale={1}>{props.children.toUpperCase()}</BitmapText>
);

// --------------------------------------------------------------- the screen

export function MapEditor(props: MapEditorProps): JSX.Element {
  usePolishStyles();

  // One reducer over one value; see `editorReducer` for why it is not three
  // `useState`s with the history threaded between them.
  // `editorFixture()` is the screenshot harness's board and is null in every
  // other path; the host's own `initial` still wins. See `fixture.ts`.
  const [history, dispatch] = useReducer(
    editorReducer, undefined, () => newHistory(props.initial ?? editorFixture() ?? blankDraft()),
  );
  const draft = history.draft;

  const [tool, setTool] = useState<EditorTool>("terrain");
  const [terrain, setTerrain] = useState<TerrainKind>("grass");
  const [brush, setBrush] = useState(1);
  const [selectedDef, setSelectedDef] = useState<PaletteEntry | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<string>(draft.players[0]?.id ?? "p1");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<PaintView>({ x: 0, y: 0, z: 0, cell: DEFAULT_ZOOM });
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);

  // A host that opened the editor on a map replaces the draft; a host that did
  // not must not have its blank draft thrown away on every re-render.
  const seeded = useRef(props.initial);
  useEffect(() => {
    if (props.initial !== undefined && props.initial !== seeded.current) {
      seeded.current = props.initial;
      dispatch({ type: "reset", draft: props.initial });
    }
  }, [props.initial]);

  const commit = useCallback((next: EditorDraft): void => {
    dispatch({ type: "commit", next });
  }, []);
  const undo = useCallback((): void => { dispatch({ type: "undo" }); }, []);
  const redo = useCallback((): void => { dispatch({ type: "redo" }); }, []);

  const water = props.waterTerrains ?? new Set(["water"]);
  const issues = useMemo(() => draftIssues(draft, water), [draft, water]);
  const blocked = hasErrors(issues);

  const palette = useMemo(
    () => (props.content === null ? [] : buildPalette(props.content)), [props.content],
  );
  const shown = useMemo(() => filterPalette(palette, query), [palette, query]);

  // ------------------------------------------------------------- the board

  const { ref: stageRef, width: stageW, height: stageH } = useElementSize<HTMLDivElement>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const art = useGroundArt();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || stageW === 0 || stageH === 0) return;
    const ratio = Math.min(2, Math.max(1, Math.round(window.devicePixelRatio || 1)));
    canvas.width = Math.floor(stageW * ratio);
    canvas.height = Math.floor(stageH * ratio);
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;
    const cursor: PaintCursor | null = hover === null || tool !== "terrain"
      ? null : { x: hover.x, y: hover.y, radius: brush };
    paintDraft(ctx, draft, view, stageW, stageH, ratio, art, cursor);
  }, [draft, view, stageW, stageH, art, hover, tool, brush]);

  /**
   * Open on a zoom that COVERS the stage, and keep the window on the map.
   *
   * `fitted` is a ref rather than state because the author is allowed to zoom
   * out afterwards: refitting on every render would fight his hand, and
   * refitting on nothing at all would leave a resized board at a zoom that
   * shows a corner of it.
   *
   * The reversal from `fitZoom` to `coverZoom` is argued where `coverZoom` is
   * defined. The short version: fitting a 48-cell board into a 1472x1024 stage
   * left 825 32x32 blocks of empty floor around it, and the whole-map view it
   * bought is better served by the navigator in the left rail.
   */
  const fitted = useRef("");
  useEffect(() => {
    if (stageW === 0 || stageH === 0) return;
    const key = `${String(draft.width)}x${String(draft.height)}@${String(Math.round(stageW))}`;
    const fresh = fitted.current !== key;
    fitted.current = key;
    setView((v) => {
      const cell = fresh ? coverZoom(draft, stageW, stageH) : v.cell;
      const seed = fresh
        ? centreOn({ ...v, cell }, draft, stageW, stageH,
                   Math.floor(draft.width / 2), Math.floor(draft.height / 2))
        : v;
      const clamped = clampView(seed, draft, stageW, stageH);
      return clamped.x === v.x && clamped.y === v.y && clamped.z === v.z
        && clamped.cell === v.cell ? v : clamped;
    });
  }, [draft, stageW, stageH]);

  /** Re-centre on what was in the middle when the zoom changes, so a step in
   *  does not throw the author to the top-left corner of the board. */
  const setZoom = useCallback((cell: number): void => {
    setView((v) => {
      const seen = visibleCells(v, stageW, stageH);
      const cx = v.x + Math.floor(seen.cols / 2);
      const cy = v.y + Math.floor(seen.rows / 2);
      return centreOn({ ...v, cell }, draft, stageW, stageH, cx, cy);
    });
  }, [draft, stageW, stageH]);

  /** Stable, so the navigator repaints when the window moves and not on every
   *  render of the screen around it. */
  const navWindow = useMemo(
    () => ({ x: view.x, y: view.y, ...visibleCells(view, stageW, stageH) }),
    [view, stageW, stageH],
  );

  const goTo = useCallback((cx: number, cy: number): void => {
    setView((v) => centreOn(v, draft, stageW, stageH, cx, cy));
  }, [draft, stageW, stageH]);

  const panBy = useCallback((dx: number, dy: number): void => {
    setView((v) => clampView({ ...v, x: v.x + dx, y: v.y + dy }, draft, stageW, stageH));
  }, [draft, stageW, stageH]);

  const apply = useCallback((x: number, y: number): void => {
    const z = view.z;
    switch (tool) {
      case "terrain": commit(paintTerrain(draft, x, y, z, terrain, brush)); return;
      case "erase": commit(removeObjectAt(draft, x, y, z)); return;
      case "object":
        if (selectedDef === null) return;
        // One object per cell: placing onto an occupied cell replaces rather
        // than stacking, because two objects on a cell is an error the
        // validator would report and nobody meant to make.
        commit(addObject(
          removeObjectAt(draft, x, y, z), selectedDef.def, selectedDef.kind, x, y, z,
        ));
        return;
      case "town": commit(setPlayerTown(draft, selectedPlayer, { x, y, z })); return;
      case "hero": commit(setPlayerHero(draft, selectedPlayer, { x, y, z })); return;
      case "inspect": return;
    }
  }, [tool, draft, view.z, terrain, brush, selectedDef, selectedPlayer, commit]);

  const painting = useRef(false);

  const at = useCallback((e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const rect = e.currentTarget.getBoundingClientRect();
    return cellFromPixel(
      draft, view, e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height,
    );
  }, [draft, view]);

  /**
   * Panning, which the board needs now that it opens covering the stage.
   *
   * The right button and the middle button both do it, because the right button
   * is where a hand that has been painting already is and the middle button is
   * what every other tile editor uses. The wheel scrolls as well, with shift for
   * the horizontal axis - a wheel that ZOOMED would step the whole board under
   * the pointer for a flick the author meant as a nudge.
   *
   * The anchor is kept in pixels and converted to whole cells on each move, so
   * a slow drag moves the window one cell at a time instead of rounding to zero
   * and never moving at all.
   */
  const panning = useRef<{ sx: number; sy: number; vx: number; vy: number } | null>(null);

  const onDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (e.button === 1 || e.button === 2) {
      panning.current = { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    const cell = at(e);
    painting.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    apply(cell.x, cell.y);
  }, [at, apply, view.x, view.y]);

  const onMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>): void => {
    const drag = panning.current;
    if (drag !== null) {
      const dx = Math.round((drag.sx - e.clientX) / view.cell);
      const dy = Math.round((drag.sy - e.clientY) / view.cell);
      setView((v) => clampView({ ...v, x: drag.vx + dx, y: drag.vy + dy }, draft, stageW, stageH));
      return;
    }
    const cell = at(e);
    // Only when the CELL changes, not on every pixel of travel. The board's
    // repaint is the expensive thing on this screen - measured in the harness's
    // browser, 1518 tiles is 19.6ms against 0.6ms for the flat colour it
    // replaced - and a fresh `{x, y}` on every `pointermove` re-ran it for a
    // brush ring that had not moved. At the zoom the editor opens on, a cell is
    // 32px, so this is one repaint per 32px of travel instead of one per pixel.
    setHover((h) => (h !== null && h.x === cell.x && h.y === cell.y ? h : cell));
    // Only the terrain brush drags. Dragging the object brush would carpet the
    // map with whatever was selected, which is never what the hand meant.
    if (painting.current && (tool === "terrain" || tool === "erase")) apply(cell.x, cell.y);
  }, [at, apply, tool, view.cell, draft, stageW, stageH]);

  const stop = useCallback((): void => {
    painting.current = false;
    panning.current = null;
  }, []);

  const onWheel = useCallback((e: React.WheelEvent<HTMLCanvasElement>): void => {
    const step = e.deltaY > 0 ? 2 : e.deltaY < 0 ? -2 : 0;
    if (step === 0) return;
    if (e.shiftKey) panBy(step, 0); else panBy(0, step);
  }, [panBy]);

  // ------------------------------------------------------------- rendering

  const player = draft.players.find((p) => p.id === selectedPlayer) ?? draft.players[0] ?? null;
  const under = hover === null ? null : draftObjectAt(draft, hover.x, hover.y, view.z);
  const insideMap = hover !== null
    && hover.x >= 0 && hover.y >= 0 && hover.x < draft.width && hover.y < draft.height;

  return (
    <div className="hs-screen" role="region" aria-label="Map editor">
      <ScreenStyles />

      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title" style={{ display: "inline-flex", alignItems: "center" }}>
          <BitmapText face="display" scale={2}>MAP EDITOR</BitmapText>
          {/* The mark is an SVG, so the screen has no text node saying what it
              is. Both a screen reader and `qa/play-editor.mjs` look for one. */}
          <span className="hs-sr">Map Editor</span>
        </span>
        <span className="hs-screen__sub hs-muted">
          {`${draft.meta.name} — ${String(draft.width)}x${String(draft.height)}`}
          {draft.levels > 1 ? ", two levels" : ""}
        </span>
        <span className="hs-spacer" />
        <button type="button" className="hs-plate hs-plate--sm" disabled={!canUndo(history)} onClick={undo}>
          Undo
        </button>
        <button type="button" className="hs-plate hs-plate--sm" disabled={!canRedo(history)} onClick={redo}>
          Redo
        </button>
        {props.onOpenFile !== undefined ? (
          <button type="button" className="hs-plate hs-plate--sm" onClick={props.onOpenFile}>Open</button>
        ) : null}
        {props.onSaveFile !== undefined ? (
          <button
            type="button"
            className="hs-plate hs-plate--sm"
            onClick={() => { props.onSaveFile?.(draft); }}
          >
            Save
          </button>
        ) : null}
        {props.onPlay !== undefined ? (
          <button
            type="button"
            className="hs-plate hs-plate--sm"
            disabled={blocked}
            title={blocked ? "Fix the errors below first" : "Start a game on this map"}
            onClick={() => { props.onPlay?.(draft); }}
          >
            Play
          </button>
        ) : null}
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      <div style={STYLE.body}>
        {/* ---- tools */}
        <div style={STYLE.rail}>
          <div className="hs-card hs-wood">
            <div className="hs-card__title"><Cap>Tool</Cap></div>
            <div className="hs-choices" role="group" aria-label="Tool">
              {(Object.keys(TOOL_LABEL) as EditorTool[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`hs-choice ${tool === t ? "hs-choice--on" : ""}`}
                  aria-pressed={tool === t}
                  title={TOOL_HINT[t]}
                  onClick={() => { setTool(t); }}
                >
                  {TOOL_LABEL[t]}
                </button>
              ))}
            </div>
            <div className="hs-tiny hs-muted">{TOOL_HINT[tool]}</div>
          </div>

          {tool === "terrain" ? (
            <div className="hs-card hs-wood">
              <div className="hs-card__title"><Cap>Ground</Cap></div>
              <div style={STYLE.swatches}>
                {TERRAINS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    title={titleCase(t)}
                    aria-label={titleCase(t)}
                    aria-pressed={terrain === t}
                    onClick={() => { setTerrain(t); }}
                    style={{
                      height: "22px", cursor: "pointer", borderRadius: "2px",
                      background: terrainSwatch(t),
                      border: terrain === t ? "2px solid #f3ddab" : "1px solid #3a2f1c",
                    }}
                  />
                ))}
              </div>
              <div style={{ marginTop: "5px", color: "#f0dfae" }}>
                <BitmapText face="text" scale={1}>{terrain.toUpperCase()}</BitmapText>
              </div>

              <div className="hs-field" style={{ marginTop: "8px" }}>
                <div className="hs-field__label"><Cap>Brush</Cap></div>
                <div className="hs-choices" role="group" aria-label="Brush size">
                  {BRUSH_SIZES.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      className={`hs-choice ${brush === b.radius ? "hs-choice--on" : ""}`}
                      aria-pressed={brush === b.radius}
                      onClick={() => { setBrush(b.radius); }}
                    >
                      {b.id}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="hs-btn"
                style={{ width: "100%", marginTop: "8px" }}
                onClick={() => { commit(fillLevel(draft, view.z, terrain)); }}
              >
                {`Fill this level with ${titleCase(terrain)}`}
              </button>
            </div>
          ) : null}

          {tool === "object" ? (
            <div className="hs-card hs-wood" style={{ minHeight: 0 }}>
              <div className="hs-card__title">
                <Cap>{`Objects (${String(paletteCount(shown))})`}</Cap>
              </div>
              <input
                type="search"
                className="hs-input"
                placeholder="Search"
                value={query}
                onChange={(e) => { setQuery(e.currentTarget.value); }}
                style={{ width: "100%", marginBottom: "6px" }}
              />
              <div className="hs-scroll" style={{ maxHeight: "340px" }}>
                {shown.map((g) => (
                  <div key={g.kind}>
                    <div className="hs-tiny hs-muted" style={{ marginTop: "6px" }}>{g.label}</div>
                    {g.entries.map((e) => (
                      <button
                        key={e.def}
                        type="button"
                        className="hs-listrow"
                        title={e.def}
                        onClick={() => { setSelectedDef(e); }}
                        style={{
                          width: "100%", textAlign: "left", cursor: "pointer",
                          border: selectedDef?.def === e.def ? "1px solid #d9b25a" : "1px solid transparent",
                        }}
                      >
                        <span className="hs-listrow__meta">{e.name}</span>
                      </button>
                    ))}
                  </div>
                ))}
                {shown.length === 0 ? (
                  <div className="hs-tiny hs-muted">
                    {props.content === null ? "Content is still loading." : "Nothing matches."}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {tool === "town" || tool === "hero" ? (
            <div className="hs-card hs-wood">
              <div className="hs-card__title"><Cap>Whose</Cap></div>
              <PlayerPicker
                players={draft.players}
                selected={selectedPlayer}
                onSelect={setSelectedPlayer}
              />
            </div>
          ) : null}

          <MapFacts draft={draft} z={view.z} />
          <DraftMinimap draft={draft} z={view.z} window={navWindow} onGo={goTo} />
          <ScenarioCard draft={draft} />
        </div>

        {/* ---- the board */}
        <div ref={stageRef} style={STYLE.stage}>
          <canvas
            ref={canvasRef}
            style={STYLE.canvas}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={stop}
            onPointerCancel={stop}
            onWheel={onWheel}
            onPointerLeave={() => { stop(); setHover(null); }}
            onContextMenu={(e) => { e.preventDefault(); }}
          />
          <div
            className="hs-tiny hs-muted"
            style={{
              position: "absolute", right: 8, bottom: 8, padding: "3px 9px",
              background: "rgba(12,10,8,0.74)", pointerEvents: "none",
              boxShadow: "inset 0 0 0 1px rgba(108,90,52,0.5)",
            }}
          >
            Right-drag or turn the wheel to move the map.
          </div>
          <div style={STYLE.readout}>
            {hover === null || !insideMap ? (
              <BitmapText face="text" scale={1}>OFF THE MAP</BitmapText>
            ) : (
              <>
                <BitmapText scale={2}>
                  {`${String(hover.x)},${String(hover.y)}`}
                </BitmapText>
                <BitmapText face="text" scale={1}>
                  {terrainAtCell(draft, hover.x, hover.y, view.z).toUpperCase()}
                </BitmapText>
                {under === null ? null : (
                  <span className="hs-tiny" style={{ color: "#d9b25a" }}>{under.def}</span>
                )}
                {/* The figures above are SVG marks, so the readout has no text
                    node in it. This is the same line as prose, for a screen
                    reader and for `qa/play-editor.mjs`, which reads the cell
                    under the pointer out of the page's own text. */}
                <span className="hs-sr">
                  {`(${String(hover.x)}, ${String(hover.y)}) `
                    + titleCase(terrainAtCell(draft, hover.x, hover.y, view.z))
                    + (under === null ? "" : ` · ${under.def}`)}
                </span>
              </>
            )}
          </div>
        </div>

        {/* ---- properties */}
        <div style={STYLE.rail}>
          <div className="hs-card hs-wood">
            <div className="hs-card__title"><Cap>Board</Cap></div>
            <div className="hs-field">
              <div className="hs-field__label"><Cap>Level</Cap></div>
              <div className="hs-choices" role="group" aria-label="Level">
                {[0, 1].slice(0, draft.levels).map((z) => (
                  <button
                    key={z}
                    type="button"
                    className={`hs-choice ${view.z === z ? "hs-choice--on" : ""}`}
                    aria-pressed={view.z === z}
                    onClick={() => { setView((v) => ({ ...v, z })); }}
                  >
                    {z === 0 ? "Surface" : "Underground"}
                  </button>
                ))}
              </div>
            </div>
            <div className="hs-field">
              <div className="hs-field__label"><Cap>Zoom</Cap></div>
              <div className="hs-choices" role="group" aria-label="Zoom">
                {ZOOM_STEPS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`hs-choice ${view.cell === c ? "hs-choice--on" : ""}`}
                    aria-pressed={view.cell === c}
                    title={`${String(c)} pixels a cell`}
                    onClick={() => { setZoom(c); }}
                  >
                    {String(c)}
                  </button>
                ))}
                <button
                  type="button"
                  className="hs-choice"
                  title="Step out until the whole board is on screen"
                  onClick={() => { setZoom(fitZoom(draft, stageW, stageH)); }}
                >
                  Fit
                </button>
              </div>
            </div>
            <div className="hs-field">
              <div className="hs-field__label"><Cap>Size</Cap></div>
              <div className="hs-choices" role="group" aria-label="Size">
                {[36, 48, 72, 108, 144].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`hs-choice ${draft.width === n ? "hs-choice--on" : ""}`}
                    aria-pressed={draft.width === n}
                    title="Grows from the top-left; nothing already placed moves"
                    onClick={() => { commit(resizeDraft(draft, n, n, draft.levels)); }}
                  >
                    {String(n)}
                  </button>
                ))}
              </div>
            </div>
            <div className="hs-field">
              <div className="hs-field__label"><Cap>Levels</Cap></div>
              <div className="hs-choices" role="group" aria-label="Levels">
                {[1, 2].map((l) => (
                  <button
                    key={l}
                    type="button"
                    className={`hs-choice ${draft.levels === l ? "hs-choice--on" : ""}`}
                    aria-pressed={draft.levels === l}
                    onClick={() => { commit(resizeDraft(draft, draft.width, draft.height, l)); }}
                  >
                    {l === 1 ? "Surface only" : "Two"}
                  </button>
                ))}
              </div>
            </div>
            {props.onGenerate !== undefined ? (
              <button
                type="button"
                className="hs-btn"
                style={{ width: "100%", marginTop: "6px" }}
                title="Fill the board from the random generator, then edit it by hand"
                onClick={() => { props.onGenerate?.(draft); }}
              >
                Generate a board
              </button>
            ) : null}
          </div>

          <div className="hs-card hs-wood">
            <div className="hs-card__title"><Cap>{`Kingdoms (${String(draft.players.length)})`}</Cap></div>
            <PlayerPicker
              players={draft.players}
              selected={selectedPlayer}
              onSelect={setSelectedPlayer}
            />
            {player !== null ? (
              <PlayerProperties
                player={player}
                content={props.content}
                onPatch={(patch) => { commit(patchPlayer(draft, player.id, patch)); }}
                onClearTown={() => { commit(setPlayerTown(draft, player.id, null)); }}
                onClearHero={() => { commit(setPlayerHero(draft, player.id, null)); }}
              />
            ) : null}
            <div className="hs-row" style={{ gap: 6, marginTop: 6 }}>
              <button
                type="button"
                className="hs-btn"
                disabled={draft.players.length >= EDITOR_COLOURS.length}
                onClick={() => { commit(addPlayer(draft)); }}
              >
                Add
              </button>
              <button
                type="button"
                className="hs-btn"
                disabled={draft.players.length <= 1 || player === null}
                onClick={() => {
                  if (player === null) return;
                  commit(removePlayer(draft, player.id));
                  setSelectedPlayer(draft.players[0]?.id ?? "p1");
                }}
              >
                Remove
              </button>
            </div>
          </div>

          {under !== null ? (
            <ObjectProperties
              object={under}
              players={draft.players}
              onOwner={(owner) => { commit(setObjectOwner(draft, under.key, owner)); }}
              onField={(field, value) => {
                commit(setObjectPayload(draft, under.key, field, value));
              }}
              onPayload={(payload) => { commit(setObjectPayloadAll(draft, under.key, payload)); }}
            />
          ) : null}

          <IssueList issues={issues} notice={props.notice ?? null} />
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- parts

/**
 * The terrain sheet, decoded once, so the board can blit the game's own tiles.
 *
 * The sheet is found through the atlas rather than by a hard-coded path: the
 * packer decides which sheet a group lands on and renumbers them whenever the
 * library grows, so asking for the frame of one known key and reading its sheet
 * back is the only way to name the file that does not go stale. A build with no
 * atlas - a test, a manifest that failed to fetch - gets `null` and the board
 * paints flat colour, which is a degradation and not a failure.
 */
function useGroundArt(): GroundArt | null {
  const atlas = useAtlas();
  const [image, setImage] = useState<HTMLImageElement | ImageBitmap | null>(null);

  const url = useMemo(() => {
    const frame = atlas.frame(terrainFillKey("grass", 0, 0));
    return frame === null ? "" : atlas.sheetUrl(frame.sheet);
  }, [atlas]);

  /**
   * Decoded to an `ImageBitmap` and not left as an `<img>`.
   *
   * Measured in the harness's browser, 1518 tiles a frame, arms interleaved so
   * a load spike lands on both: 22.0ms a frame out of the `<img>` against
   * 19.6ms out of an `ImageBitmap` (medians of 15 rounds; minima 19.3 and
   * 15.1). Modest, and free - the one-off `createImageBitmap` happens once,
   * off the paint path, while the board is still drawing flat colour. A browser
   * that will not give one falls back to the element, which is slower and
   * correct.
   *
   * An earlier version of this note claimed 59.0ms against 26.9ms. Those were
   * two single un-warmed samples taken minutes apart on a box running six other
   * sessions, and the ratio in them was mostly the first-use decode of the webp
   * landing inside whichever loop ran first.
   */
  useEffect(() => {
    if (url === "") { setImage(null); return undefined; }
    let live = true;
    const img = new Image();
    img.onload = (): void => {
      if (!live) return;
      if (typeof createImageBitmap !== "function") { setImage(img); return; }
      void createImageBitmap(img).then(
        (bmp) => { if (live) setImage(bmp); },
        () => { if (live) setImage(img); },
      );
    };
    img.onerror = (): void => { if (live) setImage(null); };
    img.src = url;
    return () => { live = false; };
  }, [url]);

  return useMemo(
    () => (image === null ? null : { image, frame: (key: string) => atlas.frame(key) }),
    [image, atlas],
  );
}

/**
 * What is on this level, as figures and as a bar of the ground's own colours.
 *
 * Not decoration. "How much of this map is water" is a question an author asks
 * on every pass and could previously only answer by looking, and the bar
 * answers it in the same colours the board is painted in - which is only true
 * now that both come out of `TERRAIN_GROUND_COLOUR`.
 */
function MapFacts(props: { readonly draft: EditorDraft; readonly z: number }): JSX.Element {
  const { draft, z } = props;
  const counts = useMemo(() => terrainCensus(draft, z), [draft, z]);
  const cells = draft.width * draft.height;
  const ranked = useMemo(
    () => TERRAINS.map((t, i) => ({ t, n: counts[i] ?? 0 }))
      .filter((e) => e.n > 0)
      .sort((a, b) => b.n - a.n),
    [counts],
  );
  const here = draft.objects.filter((o) => o.z === z).length;

  return (
    <div className="hs-card hs-wood" style={{ flex: "0 0 auto" }}>
      <div className="hs-card__title"><Cap>This level</Cap></div>
      <div style={STYLE.mixBar} title="Share of the level by ground">
        {ranked.map((e) => (
          <div
            key={e.t}
            style={{ width: `${String((e.n / cells) * 100)}%`, background: terrainSwatch(e.t) }}
            title={`${titleCase(e.t)} ${String(Math.round((e.n / cells) * 100))}%`}
          />
        ))}
      </div>
      <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "3px" }}>
        {ranked.slice(0, 4).map((e) => (
          <div key={e.t} className="hs-row" style={{ gap: 6 }}>
            <span style={{
              width: "10px", height: "10px", flex: "0 0 auto", background: terrainSwatch(e.t),
              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.7)",
            }}
            />
            <span style={{ flex: "1 1 auto", color: "#cbb98f" }}>
              <BitmapText face="text" scale={1}>{e.t.toUpperCase()}</BitmapText>
            </span>
            <span style={{ color: "#efe2c2" }}>
              <BitmapText scale={1}>{`${String(Math.round((e.n / cells) * 100))}%`}</BitmapText>
            </span>
          </div>
        ))}
      </div>
      <div className="hs-row" style={{ gap: 10, marginTop: "7px", color: "#cbb98f" }}>
        <BitmapText face="text" scale={1}>{`${String(here)} OBJECTS`}</BitmapText>
        <BitmapText face="text" scale={1}>{`${String(cells)} CELLS`}</BitmapText>
      </div>
    </div>
  );
}

/**
 * The navigator: the whole level at one texel a cell, with the board's window
 * drawn on it.
 *
 * This is what pays for the board opening zoomed in. A map editor has always
 * had one of these, and it does the job better than the board zoomed out did:
 * it is always visible, it does not change scale when the author works close
 * in, and clicking it moves the window instead of changing what the brush is
 * pointing at.
 */
function DraftMinimap(props: {
  readonly draft: EditorDraft;
  readonly z: number;
  readonly window: { readonly x: number; readonly y: number;
    readonly cols: number; readonly rows: number };
  readonly onGo: (x: number, y: number) => void;
}): JSX.Element {
  const { draft, z, window: win } = props;
  const [src, setSrc] = useState("");
  const dragging = useRef(false);

  /**
   * Painted into a DETACHED canvas and published as a data URL, rather than
   * mounted as a second `<canvas>` in the page.
   *
   * Two reasons, and the second is the load-bearing one. The picture is a fixed
   * texel grid that the box scales, so nothing about it needs a live drawing
   * surface. And `qa/play-editor.mjs` reaches the board with
   * `document.querySelector("canvas")` - a second canvas in this screen makes
   * that harness sample the navigator and report that the brush painted
   * nothing, which is a false failure on the one end-to-end test this screen
   * has.
   */
  useEffect(() => {
    if (typeof document === "undefined") return;
    const size = minimapSize(draft);
    const canvas = document.createElement("canvas");
    canvas.width = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;
    paintMinimap(ctx, draft, z, win);
    setSrc(canvas.toDataURL("image/png"));
  }, [draft, z, win]);

  const go = (e: React.PointerEvent<HTMLElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cell = minimapCell(
      draft, rect.width, rect.height, e.clientX - rect.left, e.clientY - rect.top,
    );
    props.onGo(cell.x, cell.y);
  };

  return (
    <div className="hs-card hs-wood" style={{
      flex: "0 0 auto", display: "flex", flexDirection: "column",
    }}
    >
      <div className="hs-card__title"><Cap>Whole map</Cap></div>
      <div
        role="button"
        tabIndex={0}
        aria-label="Whole map"
        style={{
          // Sized to its content: the box takes the map's own aspect, so a
          // square board is a square picture rather than a square picture in a
          // letterbox.
          flex: "0 0 auto", width: "100%",
          aspectRatio: `${String(draft.width)} / ${String(draft.height)}`,
          position: "relative", cursor: "pointer", background: "#0a0805",
          boxShadow: "inset 0 0 0 1px #0a0805, 0 0 0 1px rgba(168,128,31,0.45)",
        }}
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          go(e);
        }}
        onPointerMove={(e) => { if (dragging.current) go(e); }}
        onPointerUp={() => { dragging.current = false; }}
        onPointerLeave={() => { dragging.current = false; }}
      >
        {src === "" ? null : (
          <img
            src={src}
            alt=""
            draggable={false}
            style={{
              display: "block", width: "100%", height: "100%",
              imageRendering: "pixelated", pointerEvents: "none",
            }}
          />
        )}
      </div>
      <div className="hs-tiny hs-muted" style={{ marginTop: "5px" }}>
        Click to bring that part of the map into the board.
      </div>
    </div>
  );
}

function PlayerPicker(props: {
  readonly players: readonly DraftPlayer[];
  readonly selected: string;
  readonly onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <div className="hs-choices" role="group" aria-label="Player">
      {props.players.map((p) => (
        <button
          key={p.id}
          type="button"
          className={`hs-choice ${props.selected === p.id ? "hs-choice--on" : ""}`}
          aria-pressed={props.selected === p.id}
          title={`${titleCase(p.colour)} — ${p.kind}`}
          onClick={() => { props.onSelect(p.id); }}
          style={{ borderLeft: `4px solid ${draftPlayerColour(p)}` }}
        >
          {titleCase(p.colour)}
        </button>
      ))}
    </div>
  );
}

const PLAYER_KINDS: readonly PlayerKind[] = Object.freeze(["human", "ai", "none"]);
const KIND_LABEL: Readonly<Record<PlayerKind, string>> = Object.freeze({
  human: "Human", ai: "Computer", none: "Absent",
});

function PlayerProperties(props: {
  readonly player: DraftPlayer;
  readonly content: ContentRegistry | null;
  readonly onPatch: (patch: Partial<Omit<DraftPlayer, "id">>) => void;
  readonly onClearTown: () => void;
  readonly onClearHero: () => void;
}): JSX.Element {
  const { player } = props;
  const factions = useMemo(() => {
    if (props.content === null) return ["sunspire"];
    // A faction with no creatures is `neutral`, which nobody plays.
    return [...props.content.factions.values()]
      .filter((f) => f.creatures.length > 0)
      .map((f) => f.id as string)
      .sort();
  }, [props.content]);

  return (
    <div style={{ marginTop: "6px" }}>
      <div className="hs-field">
        <div className="hs-field__label"><Cap>Played by</Cap></div>
        <div className="hs-choices" role="group" aria-label="Played by">
          {PLAYER_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className={`hs-choice ${player.kind === k ? "hs-choice--on" : ""}`}
              aria-pressed={player.kind === k}
              onClick={() => { props.onPatch({ kind: k }); }}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
      </div>
      <div className="hs-field">
        <div className="hs-field__label"><Cap>Faction</Cap></div>
        <select
          className="hs-input"
          value={player.faction}
          onChange={(e) => { props.onPatch({ faction: e.currentTarget.value }); }}
          style={{ width: "100%" }}
        >
          {factions.map((f) => <option key={f} value={f}>{titleCase(f)}</option>)}
        </select>
      </div>
      {/* A row rather than a run of inline text: the ghost button is taller
          than the line it sat in and covered the coordinates it belonged to. */}
      <PlaceRow
        label="Capital"
        place={player.town}
        empty="No capital placed."
        onClear={props.onClearTown}
      />
      <PlaceRow
        label="Hero"
        place={player.hero}
        empty="No starting hero placed."
        onClear={props.onClearHero}
      />
    </div>
  );
}

/** Where a capital or a starting hero stands, with the button that lifts it. */
function PlaceRow(props: {
  readonly label: string;
  readonly place: { readonly x: number; readonly y: number } | null;
  readonly empty: string;
  readonly onClear: () => void;
}): JSX.Element {
  if (props.place === null) {
    return <div className="hs-tiny hs-muted">{props.empty}</div>;
  }
  return (
    <div className="hs-row" style={{ gap: 8, marginTop: "3px" }}>
      <span style={{ color: "#c9b184" }}>
        <BitmapText face="text" scale={1}>{props.label.toUpperCase()}</BitmapText>
      </span>
      <span style={{ flex: "1 1 auto", color: "#efe2c2" }}>
        <BitmapText scale={1}>
          {`${String(props.place.x)},${String(props.place.y)}`}
        </BitmapText>
      </span>
      <button type="button" className="hs-btn hs-btn--ghost" onClick={props.onClear}>
        clear
      </button>
    </div>
  );
}

/**
 * The selected object: who it belongs to, and what its payload says.
 *
 * The payload editor is the difference between placing a Gold Mine and placing
 * a Gold Mine that yields two thousand a day. Every key here is one the kernel
 * reads; the presets fill in a whole shape at once so an author does not have
 * to know the vocabulary before he can use it.
 */
function ObjectProperties(props: {
  readonly object: DraftObject;
  readonly players: readonly DraftPlayer[];
  readonly onOwner: (owner: string | null) => void;
  readonly onField: (field: string, value: ReturnType<typeof parsePayloadValue>) => void;
  readonly onPayload: (payload: DraftPayload) => void;
}): JSX.Element {
  const { object } = props;
  const [field, setField] = useState("");
  const presets = useMemo(() => presetsFor(object.kind), [object.kind]);
  const keys = useMemo(() => Object.keys(object.payload).sort(), [object.payload]);
  // Said here as well as in the issue list, because this is where the author's
  // hand is when he writes one.
  const silent = useMemo(() => silentOverrides(object), [object]);

  return (
    <div className="hs-card hs-wood">
      <div className="hs-card__title"><Cap>Object</Cap></div>
      <div className="hs-tiny">{object.def}</div>

      <div className="hs-field" style={{ marginTop: "6px" }}>
        <div className="hs-field__label"><Cap>Flagged to</Cap></div>
        <div className="hs-choices" role="group" aria-label="Owner">
          <button
            type="button"
            className={`hs-choice ${object.owner === null ? "hs-choice--on" : ""}`}
            aria-pressed={object.owner === null}
            onClick={() => { props.onOwner(null); }}
          >
            Nobody
          </button>
          {props.players.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`hs-choice ${object.owner === p.id ? "hs-choice--on" : ""}`}
              aria-pressed={object.owner === p.id}
              onClick={() => { props.onOwner(p.id); }}
            >
              {titleCase(p.colour)}
            </button>
          ))}
        </div>
      </div>

      <div className="hs-field">
        <div className="hs-field__label">
          <Cap>
            {keys.length === 0 ? "Payload (the def's own)" : `Payload (${String(keys.length)} overridden)`}
          </Cap>
        </div>
        {keys.map((k) => (
          <div key={k} className="hs-row" style={{ gap: 4, marginBottom: 3 }}>
            <span className="hs-tiny hs-muted" style={{ minWidth: "84px" }}>{k}</span>
            <input
              className="hs-input"
              style={{ flex: "1 1 auto", minWidth: 0 }}
              value={payloadText(object.payload[k] ?? null)}
              onChange={(e) => { props.onField(k, parsePayloadValue(e.currentTarget.value)); }}
            />
            <button
              type="button"
              className="hs-btn hs-btn--ghost"
              title="Back to whatever the definition says"
              onClick={() => { props.onField(k, undefined); }}
            >
              x
            </button>
          </div>
        ))}
        <div className="hs-row" style={{ gap: 4 }}>
          <input
            className="hs-input"
            style={{ flex: "1 1 auto", minWidth: 0 }}
            placeholder="new key"
            value={field}
            onChange={(e) => { setField(e.currentTarget.value); }}
          />
          <button
            type="button"
            className="hs-btn"
            disabled={field.trim() === ""}
            onClick={() => { props.onField(field.trim(), 0); setField(""); }}
          >
            Add
          </button>
        </div>
        <div className="hs-tiny hs-muted">
          A whole number is stored as a number, true/false as a flag, and anything else
          as text. An empty box clears the key back to the definition's own value.
        </div>
        {silent.length > 0 ? (
          <div className="hs-tiny" style={{ color: "#d8c188", marginTop: "4px" }}>
            {`${silent.join(", ")} will be paid out but not advertised: the computer's `}
            planner and the player&apos;s own hover plate both price this object off its
            definition. Change the definition instead if you want either of them to know.
          </div>
        ) : null}
      </div>

      {presets.length > 0 ? (
        <div className="hs-field">
          <div className="hs-field__label"><Cap>Presets</Cap></div>
          <select
            className="hs-input"
            style={{ width: "100%" }}
            value=""
            onChange={(e) => {
              const p = presets.find((q) => q.id === e.currentTarget.value);
              if (p !== undefined) props.onPayload({ ...object.payload, ...p.payload });
              e.currentTarget.value = "";
            }}
          >
            <option value="">Add a preset...</option>
            {presets.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <div className="hs-tiny hs-muted">
            A preset adds its keys on top of whatever is already here; nothing is removed.
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * What the file will say about itself: its name, the seed it was generated
 * from, and how it is won.
 *
 * All three are already in the draft and were only ever visible as a fragment
 * of the title bar. The seed is the one that matters in practice - "Generate a
 * board" prints it into a notice that the next notice overwrites, and without
 * it a board an author liked is not reproducible.
 */
function ScenarioCard(props: { readonly draft: EditorDraft }): JSX.Element {
  const { draft } = props;
  // Size and levels are stated twice already - in the title bar and in the
  // Board card - so they are not restated here. The seed is the one nothing
  // else keeps: "Generate a board" prints it into a notice that the next
  // notice overwrites, and without it a board an author liked is gone.
  const rows: readonly (readonly [string, string])[] = [
    ["NAME", draft.meta.name],
    ["SEED", String(draft.seed)],
    ["WON BY", draft.victory.replace(/_/g, " ").toUpperCase()],
  ];
  return (
    <div className="hs-card hs-wood" style={{ flex: "0 0 auto" }}>
      <div className="hs-card__title"><Cap>Scenario</Cap></div>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {rows.map(([label, value]) => (
          <div key={label} className="hs-row" style={{ gap: 8, alignItems: "flex-start" }}>
            <span style={{ minWidth: "58px", flex: "0 0 auto", color: "#c9b184" }}>
              <BitmapText face="text" scale={1}>{label}</BitmapText>
            </span>
            <span style={{ flex: "1 1 auto", minWidth: 0, color: "#efe2c2" }}>
              <BitmapText face="text" scale={1}>{value.toUpperCase()}</BitmapText>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IssueList(props: {
  readonly issues: readonly DraftIssue[];
  readonly notice: string | null;
}): JSX.Element {
  const errors = props.issues.filter((i) => i.level === "error");
  const warnings = props.issues.filter((i) => i.level === "warning");
  return (
    <div className="hs-card hs-wood">
      <div className="hs-card__title">
        <Cap>{errors.length === 0 ? "This map can be played" : `${String(errors.length)} to fix`}</Cap>
      </div>
      {props.notice !== null ? (
        <div className="hs-tiny" style={{ color: "#f3ddab" }}>{props.notice}</div>
      ) : null}
      {props.issues.length === 0 ? (
        <div className="hs-tiny hs-muted">Nothing to report.</div>
      ) : (
        <div className="hs-list">
          {[...errors, ...warnings].map((i, n) => (
            <div className="hs-listrow" key={`${String(n)}:${i.text}`} style={{ cursor: "default" }}>
              <span
                style={{
                  // `.hs-listrow` centres its children, which put a one-line
                  // tag halfway down a four-line note and left 52px of empty
                  // plate above it. A label belongs at the top of what it
                  // labels.
                  minWidth: "44px", flex: "0 0 auto", alignSelf: "flex-start",
                  paddingTop: "3px",
                  color: i.level === "error" ? "#e08a6a" : "#d8c188",
                }}
              >
                <BitmapText face="text" scale={1}>{i.level === "error" ? "ERROR" : "NOTE"}</BitmapText>
              </span>
              <span className="hs-listrow__meta">{i.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


